import { skipToken } from "@reduxjs/toolkit/query";
import type {
  GenesysFailedImportsCount,
  GenesysFailedImportsCountResponse,
  GenesysFailedImportsCountsByCode,
  GenesysFailedImportsCountsByCodeResponse,
  GenesysFailedImportsPage,
  GenesysFailedImportsPageResponse,
  GenesysRecordingBulkRequeueResponse,
  GenesysRecordingBulkRequeueResult,
  GenesysRecordingBulkSkipResponse,
  GenesysRecordingBulkSkipResult,
  GenesysRecordingId,
  GenesysRecordingSortOrder,
} from "@repo/app-types/entities/genesysRecording";
import {
  IntegrationProvider,
  type GenesysIntegration,
  type GenesysIntegrationRequest,
  type IntegrationConnectionTestResponse,
} from "@repo/app-types/entities/integration";
import { buildEntityTag, TagType } from "../helpers/tags.js";
import { api } from "../redux.js";

/**
 * The Genesys integration comes from its own route rather than the boot
 * payload: the stored settings carry the encrypted client secret, which no
 * customer response may include, so the API returns a non-secret view instead.
 *
 * The secret travels one way only — every credential write posts it again, and
 * nothing ever reads it back, so the detail view renders a fixed mask.
 */

export interface GenesysIntegrationResponse {
  integration: GenesysIntegration | null;
}

const GENESYS_TAG_ID = "genesys";

// A separate tag id for the failed-imports list + count, so reprocessing or
// skipping a row refetches those without disturbing the integration-status
// query (which those actions don't change), and vice versa.
const GENESYS_FAILED_IMPORTS_TAG_ID = "genesys-failed-imports";

export const genesysIntegrationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    genesysIntegration: builder.query<GenesysIntegrationResponse, void>({
      query: () => "/integrations/genesys",
      providesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    /**
     * Probes typed credentials without storing them. Always carries the full
     * credentials because the stored secret is write-only — there is no
     * server-side test to run against a connection the customer isn't editing.
     */
    testGenesysConnection: builder.mutation<
      IntegrationConnectionTestResponse,
      GenesysIntegrationRequest
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations/test-connection",
        body: { provider: IntegrationProvider.GENESYS, ...credentials },
      }),
    }),

    connectGenesysIntegration: builder.mutation<
      GenesysIntegration,
      GenesysIntegrationRequest,
      { integration: GenesysIntegration }
    >({
      query: (credentials) => ({
        method: "POST",
        url: "/integrations",
        body: { provider: IntegrationProvider.GENESYS, ...credentials },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    updateGenesysIntegration: builder.mutation<
      GenesysIntegration,
      { integrationId: string; credentials: GenesysIntegrationRequest },
      { integration: GenesysIntegration }
    >({
      query: ({ integrationId, credentials }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: credentials,
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    setGenesysIntegrationEnabled: builder.mutation<
      GenesysIntegration,
      { integrationId: string; isEnabled: boolean },
      { integration: GenesysIntegration }
    >({
      // Shares the credential route: the union body means pausing never has to
      // carry the client secret.
      query: ({ integrationId, isEnabled }) => ({
        method: "PATCH",
        url: `/integrations/${integrationId}`,
        body: { is_enabled: isEnabled },
      }),
      transformResponse: (response) => response.integration,
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    disconnectGenesysIntegration: builder.mutation<
      void,
      { integrationId: string }
    >({
      query: ({ integrationId }) => ({
        method: "DELETE",
        url: `/integrations/${integrationId}`,
      }),
      invalidatesTags: () => [
        buildEntityTag(TagType.GenesysIntegration, GENESYS_TAG_ID),
      ],
    }),

    // The customer "Failed imports" tab: the caller's own `failed` rows, which
    // the server scopes to their company. Both args are part of the cache key so
    // changing either starts a fresh page 1: `order` is the `recorded_at` sort;
    // `errorCode` is the selected chip — `null` = every code, the `__none__`
    // sentinel = rows with no code, else a literal code (forwarded verbatim, the
    // server maps the sentinel back to "no code").
    genesysFailedImports: builder.infiniteQuery<
      GenesysFailedImportsPage,
      { order: GenesysRecordingSortOrder; errorCode: string | null },
      string | null,
      GenesysFailedImportsPageResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/integrations/genesys/failed-imports",
        params: {
          order: queryArg.order,
          ...(queryArg.errorCode ? { errorCode: queryArg.errorCode } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
      transformResponse: (response) => response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.GenesysIntegration,
          GENESYS_FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The filter chips' counts: `failed`-row counts by `error.code`, scoped to
    // the caller's company and *not* to the selected chip, so picking a chip
    // doesn't refetch (and blank) them. Shares the failed-imports tag, so a
    // reprocess/skip that empties a code refetches this and drops its chip.
    genesysFailedImportsCountsByCode: builder.query<
      GenesysFailedImportsCountsByCode,
      void
    >({
      query: () => "/integrations/genesys/failed-imports/counts",
      transformResponse: (response: GenesysFailedImportsCountsByCodeResponse) =>
        response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.GenesysIntegration,
          GENESYS_FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    // The failed count, for the tab badge. Its own read so a page of the list
    // isn't needed to know whether the badge should show.
    genesysFailedImportsCount: builder.query<GenesysFailedImportsCount, void>({
      query: () => "/integrations/genesys/failed-imports/count",
      transformResponse: (response: GenesysFailedImportsCountResponse) =>
        response.data,
      providesTags: () => [
        buildEntityTag(
          TagType.GenesysIntegration,
          GENESYS_FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    reprocessGenesysFailedImports: builder.mutation<
      GenesysRecordingBulkRequeueResult,
      readonly GenesysRecordingId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/genesys/failed-imports/reprocess",
        body: { ids },
      }),
      transformResponse: (response: GenesysRecordingBulkRequeueResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.GenesysIntegration,
          GENESYS_FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),

    skipGenesysFailedImports: builder.mutation<
      GenesysRecordingBulkSkipResult,
      readonly GenesysRecordingId[]
    >({
      query: (ids) => ({
        method: "POST",
        url: "/integrations/genesys/failed-imports/skip",
        body: { ids },
      }),
      transformResponse: (response: GenesysRecordingBulkSkipResponse) =>
        response.data,
      invalidatesTags: () => [
        buildEntityTag(
          TagType.GenesysIntegration,
          GENESYS_FAILED_IMPORTS_TAG_ID,
        ),
      ],
    }),
  }),
});

/** Subscribes to the status only when Genesys is actually on for this company. */
export function useGenesysIntegration(enabled: boolean) {
  return genesysIntegrationApi.useGenesysIntegrationQuery(
    enabled ? undefined : skipToken,
    { refetchOnMountOrArgChange: true },
  );
}

export const {
  useConnectGenesysIntegrationMutation,
  useDisconnectGenesysIntegrationMutation,
  useSetGenesysIntegrationEnabledMutation,
  useTestGenesysConnectionMutation,
  useUpdateGenesysIntegrationMutation,
  useGenesysFailedImportsInfiniteQuery,
  useGenesysFailedImportsCountQuery,
  useGenesysFailedImportsCountsByCodeQuery,
  useReprocessGenesysFailedImportsMutation,
  useSkipGenesysFailedImportsMutation,
} = genesysIntegrationApi;
